// Browser-safe catalog of the Veralta Health clinical gigs: id, label, blurb,
// the data-entry field spec, and the conformance teaching hint.
//
// Mirrors gig-catalog.ts (the Torbridge catalog) in shape and role, but lists
// the clinical gigs surfaced only in the Veralta Health app. It imports NOTHING
// heavy — no profile/schema JSON, no seal path, no Node built-ins — so both the
// Veralta browser UI and its /api/run Function can import it. It is the single
// source of truth for the clinical gig ids and input shape:
//   - the UI renders the record-centric data-entry form from `fields`,
//   - the Veralta /api/run validates the submitted inputs against `fields`,
//   - src/live/gigs-clinical.ts (the seal-bound gigs) reuse these ids.
// getGig (src/live/gigs.ts) already resolves the clinical gigs, and a test
// asserts every clinical catalog id resolves to a gig so the two cannot drift.
//
// The seal-side gigs read: prior-auth -> patientRef, providerRef, payer,
// serviceCodes, record; record-scan -> patientRef, requestedCondition, record,
// injectBreak. The `record` textarea is the editable patient/provider record;
// it is HASHED into evidence (record_hash), never sealed raw, so no PHI leaves
// the browser inside the receipt.

import type { GigCatalogEntry } from "./gig-catalog.js";

// Fictional patient records shown in the data-entry screen. Realistic in shape
// (problem list, meds, note) but entirely invented — the disclosure banner says
// so, and only a hash of whatever text is present is ever sealed.
const PRIOR_AUTH_RECORD = [
  "PATIENT: Jordan Ellery Rivas   MRN 55-402-118   DOB 1968-04-12   Sex F",
  "PROVIDER: Dr. Amara Okafor, Endocrinology (NPI 1720394857)",
  "PLAN: Veralta Health Plan   Member 8841-22C",
  "",
  "PROBLEM LIST:",
  "  - Type 2 diabetes mellitus, uncontrolled (E11.65)",
  "  - Essential hypertension (I10)",
  "",
  "MEDICATIONS TRIED: metformin 1000mg BID (>= 3 months), glipizide 10mg daily.",
  "Most recent A1c 9.4% despite adherence; fasting glucose 210-260 mg/dL.",
  "",
  "REQUEST: Prior authorization for a GLP-1 receptor agonist (semaglutide,",
  "HCPCS J1885 admin + E/M 99213). First-line therapy documented as failed.",
  "Clinical rationale: persistent hyperglycemia on max-tolerated oral agents."
].join("\n");

const SCAN_RECORD = [
  "PATIENT: Priya N. Deshmukh   MRN 71-330-905   DOB 1955-11-03   Sex F",
  "PROVIDER: Dr. Lucas Feng, Internal Medicine",
  "ENCOUNTER: 2026-06-18, annual wellness visit",
  "",
  "PROBLEM LIST:",
  "  - Type 2 diabetes mellitus (E11.9)",
  "  - Hyperlipidemia (E78.5)",
  "  - Osteoarthritis, bilateral knees (M17.0)",
  "",
  "HISTORY OF PRESENT ILLNESS:",
  "Patient reports stable energy. Home glucose log shows fasting 130-150 mg/dL.",
  "No polyuria or polydipsia. Continues metformin 500mg BID.",
  "",
  "ASSESSMENT: Type 2 diabetes, reasonably controlled on metformin. Continue",
  "current regimen; recheck A1c in 3 months. Lipids at goal on statin."
].join("\n");

export const CLINICAL_GIG_CATALOG: GigCatalogEntry[] = [
  {
    id: "prior_auth_clinical",
    label: "Prior authorization: submit to payer",
    blurb:
      "The agent reviews the patient record, decides whether to submit a prior-authorization request, and seals the submission, bound to an allowed payer list. The record is sent to the agent (this site's server and the model) to review, but only a hash of it is sealed into the receipt. The receipt carries no PHI. Use fictional data only.",
    usesReview: true,
    runLabel: "Submit prior authorization",
    fields: [
      {
        key: "record",
        label: "Patient record (editable)",
        type: "textarea",
        default: PRIOR_AUTH_RECORD,
        help: "Sent to the agent (server + model) to review and hashed into the receipt (record_hash); the raw text is never sealed. Enter fictional data only."
      },
      {
        // A closed set (no free text) so the sealed payer can never carry PHI.
        // The first two are on the plan's allowlist; the last is the
        // deterministic violation lever. Keep in sync with CLINICAL_PAYER_ALLOWLIST
        // in gigs-clinical.ts (gig-catalog stays free of the seal path).
        key: "payer",
        label: "Payer",
        type: "select",
        default: "Veralta Health Plan",
        options: ["Veralta Health Plan", "Meridian PPO", "Unlisted Payer LLC"],
        help: "Allowlisted: Veralta Health Plan, Meridian PPO. “Unlisted Payer LLC” is off-allowlist."
      },
      {
        // A closed allowlist of demo CPT/HCPCS codes (not a mere character
        // filter): the Function rejects any csv item not in `options`, so a
        // presenter cannot seal an MRN or a name as a "service code".
        key: "serviceCodes",
        label: "Service codes",
        type: "csv",
        default: "99213, J1885",
        options: ["99213", "99214", "99244", "J1885", "J1817", "J3490", "70450", "80053", "83036"],
        help: "Comma-separated demo CPT/HCPCS codes. Allowed: 99213, 99214, 99244, J1885, J1817, J3490, 70450, 80053, 83036."
      },
      {
        key: "patientRef",
        label: "Patient reference",
        type: "text",
        default: "patient-ref-8f3a",
        help: "An opaque id/hash, not the patient's name."
      },
      {
        key: "providerRef",
        label: "Provider reference",
        type: "text",
        default: "provider-ref-31a",
        help: "An opaque id/hash, not the provider's name."
      }
    ],
    violationHint:
      "Set the payer to one not on the allowlist (e.g. “Unlisted Payer LLC”) to seal a receipt that honestly records conformant: false."
  },
  {
    id: "record_scan",
    label: "Record scan: find a condition",
    blurb:
      "The agent scans the record for ONE authorized condition and returns grounded findings, each citing where in the record it appears. The mandate binds the authorized condition; a finding for anything else records conformant: false. Only a hash of the record and of each cited span is sealed.",
    usesReview: true,
    runLabel: "Scan record",
    fields: [
      {
        key: "record",
        label: "Patient record (editable)",
        type: "textarea",
        default: SCAN_RECORD,
        help: "Sent to the agent (server + model) to scan and hashed into the receipt (record_hash); the raw text is never sealed. Enter fictional data only."
      },
      {
        // A closed set (no free text) so the sealed/bound condition can never
        // carry PHI, and can never collide with the out-of-scope sentinel label.
        key: "requestedCondition",
        label: "Scan for condition",
        type: "select",
        default: "type 2 diabetes",
        options: ["type 2 diabetes", "hypertension", "hyperlipidemia"],
        help: "The one authorized condition. Every finding must be labeled with exactly this."
      },
      {
        key: "patientRef",
        label: "Patient reference",
        type: "text",
        default: "patient-ref-7b21",
        help: "An opaque id/hash, not the patient's name."
      },
      {
        key: "injectBreak",
        label: "Inject a demo violation (out-of-scope finding)",
        type: "boolean",
        default: "false",
        help: "Appends a clearly out-of-scope finding so the verifier reliably records conformant: false, a tamper demo independent of the model."
      }
    ],
    violationHint:
      "Turn on the demo-violation toggle, or write an instruction that pushes the agent to flag a different condition, to seal a receipt that records conformant: false."
  }
];

export function clinicalCatalogEntry(id: string | undefined): GigCatalogEntry | undefined {
  if (id === undefined) return CLINICAL_GIG_CATALOG[0];
  return CLINICAL_GIG_CATALOG.find((g) => g.id === id);
}
